import { useState, useEffect } from 'react';
import { createFileRoute, useSearch, Link } from '@tanstack/react-router';
import { z } from 'zod';
import {
  usePostTenantsInvitationsAcceptMutation,
  useLazyGetTenantsInvitationsVerifyQuery,
  useLazyGetTenantsByIdQuery,
} from '@/services';
import { useAuth } from '@/contexts/auth-context';
import { toast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Building, XCircle, LogIn, Loader2, AlertTriangle } from 'lucide-react';
import FullPageLoader from '@/components/full-page-loader';
import { useTenant } from '@/contexts/tenant-context';

export const Route = createFileRoute('/_neutral/accept-invitation')({
  component: AcceptInvitationPage,
  pendingComponent: FullPageLoader,
  validateSearch: z.object({
    token: z.string().optional(),
  }),
});

function InvitationStatus({
  status,
  invitationDetails,
  isAuthenticated,
  isAccepting,
  onAccept,
}: {
  status: 'loading' | 'invalid' | 'expired' | 'valid' | 'already_accepted';
  invitationDetails: {
    tenant_name: string | null;
    role: string | null;
    email: string | null;
    is_user_created: boolean;
  } | null;
  isAuthenticated: boolean;
  isAccepting: boolean;
  onAccept: () => void;
}) {
  const { token } = useSearch({ strict: false });

  switch (status) {
    case 'loading':
      return (
        <div className="flex flex-col items-center py-8">
          <div className="animate-pulse flex justify-center items-center w-16 h-16 rounded-full bg-primary/20 mb-4">
            <Building className="h-8 w-8 text-primary/60" />
          </div>
          <h3 className="text-lg font-medium mb-2">Verifying Invitation</h3>
          <p className="text-muted-foreground">Please wait while we verify your invitation</p>
        </div>
      );

    case 'invalid':
      return (
        <div className="flex flex-col items-center py-8">
          <div className="flex justify-center items-center w-16 h-16 rounded-full bg-destructive/10 mb-4">
            <XCircle className="h-8 w-8 text-destructive" />
          </div>
          <h3 className="text-lg font-medium mb-2">Invalid Invitation</h3>
          <p className="text-muted-foreground text-center mb-6">
            The invitation link is invalid or has been revoked. Please contact the organization administrator for
            assistance.
          </p>
          <Button variant="outline" onClick={() => window.location.replace('/dashboard')}>
            Go to Dashboard
          </Button>
        </div>
      );

    case 'expired':
      return (
        <div className="flex flex-col items-center py-8">
          <div className="flex justify-center items-center w-16 h-16 rounded-full bg-amber-100 mb-4">
            <AlertTriangle className="h-8 w-8 text-amber-600" />
          </div>
          <h3 className="text-lg font-medium mb-2">Expired Invitation</h3>
          <p className="text-muted-foreground text-center mb-6">
            This invitation has expired. Please contact the organization administrator for a new invitation.
          </p>
          <Button variant="outline" onClick={() => window.location.replace('/dashboard')}>
            Go to Dashboard
          </Button>
        </div>
      );

    case 'already_accepted':
      return (
        <div className="flex flex-col items-center py-8">
          <div className="flex justify-center items-center w-16 h-16 rounded-full bg-green-100 mb-4">
            <Building className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-lg font-medium mb-2">Invitation Already Accepted</h3>
          <p className="text-muted-foreground text-center mb-6">
            This invitation has already been accepted. You should now have access to the organization.
          </p>
          <Button variant="outline" onClick={() => window.location.replace('/dashboard')}>
            Go to Dashboard
          </Button>
        </div>
      );

    case 'valid':
      return (
        <div className="flex flex-col items-center py-6">
          <div className="flex justify-center items-center w-16 h-16 rounded-full bg-green-100 mb-4">
            <Building className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-xl font-medium mb-4">Invitation to Join</h3>

          <div className="bg-muted/50 w-full p-4 rounded-lg mb-6">
            <p className="text-center mb-2">You've been invited to join</p>
            <p className="text-xl font-bold text-center mb-2">{invitationDetails?.tenant_name}</p>
            <p className="text-center text-muted-foreground">
              as a <span className="font-medium">{invitationDetails?.role}</span>
            </p>
          </div>

          {isAuthenticated ? (
            <Button onClick={onAccept} className="w-full" disabled={isAccepting}>
              {isAccepting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Accepting...
                </>
              ) : (
                'Accept Invitation'
              )}
            </Button>
          ) : (
            <>
              {invitationDetails?.is_user_created ? (
                <div className="space-y-4 w-full">
                  <Button variant="outline" className="w-full" asChild>
                    <Link
                      to="/login"
                      search={{
                        redirect: window.location.pathname + (token ? `?token=${token}` : ''),
                      }}
                    >
                      <LogIn className="mr-2 h-4 w-4" />
                      Login to Accept
                    </Link>
                  </Button>
                  <p className="text-sm text-center text-muted-foreground">
                    You need to be logged in to accept this invitation
                  </p>
                </div>
              ) : (
                <div className="space-y-4 w-full">
                  <Button variant="outline" className="w-full" asChild>
                    <Link
                      to="/signup"
                      search={{
                        redirect: window.location.pathname + (token ? `?token=${token}` : ''),
                      }}
                    >
                      <LogIn className="mr-2 h-4 w-4" />
                      Sign Up to Accept
                    </Link>
                  </Button>
                  <p className="text-sm text-center text-muted-foreground">
                    You need to be signed up and logged in to accept this invitation
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      );
  }
}

function AcceptInvitationPage() {
  const { token } = useSearch({ strict: false });
  const { isAuthenticated } = useAuth();

  const [status, setStatus] = useState<'loading' | 'invalid' | 'expired' | 'valid' | 'already_accepted'>('loading');
  const [invitationAccepted, setInvitationAccepted] = useState(false);
  const [invitationDetails, setInvitationDetails] = useState<{
    tenant_name: string | null;
    role: string | null;
    email: string | null;
    is_user_created: boolean;
    tenant_id: string | null;
  } | null>(null);

  // API hooks
  const [verifyInvitation] = useLazyGetTenantsInvitationsVerifyQuery();
  const [acceptInvitation, { isLoading: isAccepting }] = usePostTenantsInvitationsAcceptMutation();
  const [getTenantById] = useLazyGetTenantsByIdQuery();
  const { setCurrentTenant } = useTenant();

  // Verify invitation token on mount
  useEffect(() => {
    async function verifyToken() {
      // Skip verification if invitation was already accepted
      if (invitationAccepted) {
        return;
      }

      if (!token) {
        toast({
          variant: 'error',
          title: 'Invalid Invitation',
          description: 'No invitation token was provided',
        });
        setStatus('invalid');
        return;
      }

      try {
        const result = await verifyInvitation({
          token: token as string,
        }).unwrap();
        console.log('Invitation verification result:', result);

        if (!result.data.valid) {
          if (result.data.already_accepted) {
            setStatus('already_accepted');
            toast({
              title: 'Invitation Already Used',
              description: 'This invitation has already been accepted.',
            });
          } else {
            setStatus('invalid');
            toast({
              variant: 'error',
              title: 'Invalid Invitation',
              description: 'This invitation link is invalid or has been revoked.',
            });
          }
          return;
        }

        if (result.data.expired) {
          setStatus('expired');
          toast({
            variant: 'warning',
            title: 'Expired Invitation',
            description: 'This invitation has expired. Please request a new one.',
          });
          return;
        }

        setInvitationDetails({
          tenant_name: result.data.tenant_name,
          role: result.data.role,
          tenant_id: result.data.tenant_id,
          email: result.data.email,
          is_user_created: !!result.data.is_user_created,
        });
        setStatus('valid');
      } catch (error) {
        console.error('Error verifying invitation:', error);
        setStatus('invalid');
        toast({
          variant: 'error',
          title: 'Verification Failed',
          description: 'Could not verify the invitation',
        });
      }
    }

    verifyToken();
  }, [token, verifyInvitation, invitationAccepted]);

  const handleAcceptInvitation = async () => {
    if (!token || !isAuthenticated) {
      if (!isAuthenticated) {
        toast({
          variant: 'warning',
          title: 'Authentication Required',
          description: 'Please login to accept this invitation',
        });
      }
      return;
    }

    try {
      const result = await acceptInvitation({
        body: { token: token as string },
      }).unwrap();

      toast({
        title: 'Invitation Accepted',
        description: `You've been added to ${result.data.tenant_name} as ${result.data.role}`,
      });

      // Mark invitation as accepted to prevent re-verification
      setInvitationAccepted(true);

      // Set tenant to recently accepted tenant
      await setNewTenant(result.data.tenant_id, result.data.tenant_name);

      // Use window.location.replace to prevent going back to invitation page
      window.location.replace('/dashboard');
    } catch (error) {
      console.error('Error accepting invitation:', error);
      toast({
        variant: 'error',
        title: 'Error Accepting Invitation',
        description: 'Failed to accept invitation. Please try again.',
      });
    }
  };

  async function setNewTenant(tenant_id: string, tenant_name: string) {
    try {
      const res = await getTenantById({ id: tenant_id }).unwrap();
      if (!res.success) {
        toast({
          variant: 'error',
          title: 'Tenant Not Found',
          description: 'Failed to find tenant ' + tenant_name,
        });
      }
      setCurrentTenant(res.data);
    } catch (_error) {
      toast({
        variant: 'error',
        title: 'Tenant Not Found',
        description: 'Failed to find tenant ' + tenant_name,
      });
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-muted/30">
      <Card className="w-full max-w-md shadow-lg border-muted">
        <CardHeader>
          <CardTitle className="text-center">Organization Invitation</CardTitle>
          <CardDescription className="text-center">Join a Web IoT Pro workspace</CardDescription>
        </CardHeader>

        <CardContent>
          <InvitationStatus
            status={status}
            invitationDetails={invitationDetails}
            isAuthenticated={isAuthenticated}
            isAccepting={isAccepting}
            onAccept={handleAcceptInvitation}
          />
        </CardContent>

        <CardFooter className="flex justify-center border-t pt-4">
          <p className="text-xs text-muted-foreground">
            Web IoT Pro enables organizations to collaborate on IoT projects
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
